import React, { useEffect } from "react";
import ErrorBoundaryCard from "~/components/ErrorBoundaryCard";
import { Transition, TransitionGroup } from "react-transition-group";
import styled, { useTheme } from "styled-components";
import WorkingStyleCard from "~/components/WorkingStyleCard";
import { enterSideBar, exitSideBar } from "~/lib/animations";
import { useDispatch, useSelector } from "react-redux";
import workingStyleSelectors from "~/store/workingStyles/selectors";
import { CRUDActionEnum, StatusStateEnum } from "~/types/enums";
import workingStylesActionCreators from "~/store/workingStyles/actions";
import { useParams } from "react-router-dom";
import ErrorSection from "~/components/ErrorSection";
import { Section, Spinner, Icon, Button, Modal } from "~/components-shared";
import { useTranslation } from "react-i18next";
import PublicWorkingStyleSideBar from "~/components/PublicWorkingStyleSideBar";
import Branding from "~/components/Branding";
import Footer from "~/components/Footer";
import WorkingStyleFeedbackModal from "~/components/WorkingStyleFeedbackModal";
const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  max-width: 1440px;
  min-width: 1266px;
  margin: 0 auto;
  background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.background.primary;
}};
`;
const Left = styled.aside`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 18.6rem;
  min-width: 18.6rem;
  max-width: 18.6rem;
  position: fixed;
  left: 50%;
  margin-left: -51.4rem;
  top: 0;
  z-index: 10;
  border-right: 1px solid ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.colors.borders.primary;
}};
  background-color: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.colors.background.primary;
}};
  padding-top: 3.5rem;

  @media (max-width: 1440px) {
    left: 0;
    margin-left: 0;
  }
`;
const Right = styled.aside`
  position: fixed;
  right: 50%;
  top: 0;
  margin-right: -51.4rem;
  background-color: ${_ref4 => {
  let {
    theme
  } = _ref4;
  return theme.colors.background.primary;
}};
  z-index: 1;

  @media (max-width: 1440px) {
    right: 0;
    margin-right: 0;
  }
`;
const Content = styled.div`
  flex-grow: 1;
  width: 100%;
  padding-left: 18.5rem;
  padding-right: 28.6rem;
`;
const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  padding: 3.6rem;
  height: 100vh;
  width: 28.6rem;
  min-width: 28.6rem;
  max-width: 28.6rem;
  border-left: 1px solid ${_ref5 => {
  let {
    theme
  } = _ref5;
  return theme.colors.borders.primary;
}};
  position: relative;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  transition: width 1300ms ease-in-out;

  &::-webkit-scrollbar {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    display: none;
  }
  &:hover::-webkit-scrollbar-thumb {
    display: initial;
  }
`;
const SectionList = styled.ul`
  margin: 0;
  list-style: none;
  overflow: auto;
  width: 100%;
  flex-grow: 1;
  transition: all 300ms ease-out;
  padding: 0 1.5rem;

  &::-webkit-scrollbar {
    display: none;
  }
  &::-webkit-scrollbar-thumb {
    display: none;
  }
  &:hover::-webkit-scrollbar-thumb {
    display: initial;
  }
`;
const List = styled.ul`
  padding: 0;
  margin: 0 0 1rem;
  list-style: none;
  width: 100%;
`;
const ListItem = styled.li`
  margin: 0;
`;
const SectionListItem = styled.li`
  margin: 0;
  padding: 0;
`;
const SectionText = styled.span`
  display: inline-block;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  padding: 0 1rem;
  color: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.colors.borders.secondary;
}};
`;
const PublicWorkingStyle = () => {
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const {
    workingStyleId
  } = useParams();
  const workingStyle = useSelector(workingStyleSelectors.getWorkingStyle(workingStyleId));
  const action = useSelector(workingStyleSelectors.getAction);
  const status = useSelector(workingStyleSelectors.getStatusByWorkingStyle(workingStyleId));
  const error = useSelector(workingStyleSelectors.getErrorByWorkingStyle(workingStyleId));
  const dispatch = useDispatch();
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(workingStylesActionCreators.fetchOneStarted(workingStyleId));
    }
  }, [dispatch, status]);
  if (!workingStyle && status === StatusStateEnum.FAILED) {
    return /*#__PURE__*/React.createElement(ErrorSection, {
      error: error
    });
  }
  if (!workingStyle) {
    return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Spinner, {
      center: true
    }));
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Wrapper, null, /*#__PURE__*/React.createElement(Left, null, /*#__PURE__*/React.createElement(Branding, {
    s: {
      px: 7,
      pb: 5
    }
  }), /*#__PURE__*/React.createElement(SectionList, null, /*#__PURE__*/React.createElement(SectionListItem, null, /*#__PURE__*/React.createElement(SectionText, null, t("LeftSideBar.menu.sections.howIWork")), /*#__PURE__*/React.createElement(List, null, /*#__PURE__*/React.createElement(ListItem, null, /*#__PURE__*/React.createElement(Button, {
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      src: "document",
      color: colors.background.primary,
      scale: 5,
      s: {
        mr: 2
      }
    }),
    color: "secondary",
    s: {
      mb: 1
    },
    textAlign: "left",
    fullWidth: true
  }, t("LeftSideBar.menu.items.myWorkingStyle"))))))), /*#__PURE__*/React.createElement(Content, null, /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(WorkingStyleCard, {
    workingStyle: workingStyle
  }))), /*#__PURE__*/React.createElement(Footer, null))), /*#__PURE__*/React.createElement(Right, null, /*#__PURE__*/React.createElement(TransitionGroup, {
    component: null
  }, /*#__PURE__*/React.createElement(Transition, {
    key: "sidebar",
    onEnter: node => enterSideBar(node),
    onExit: node => exitSideBar(node),
    timeout: {
      enter: 750,
      exit: 400
    },
    appear: true
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Sidebar, null, /*#__PURE__*/React.createElement(ErrorBoundaryCard, null, /*#__PURE__*/React.createElement(PublicWorkingStyleSideBar, {
    workingStyle: workingStyle
  }))))))), /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.FEEDBACK
  }, /*#__PURE__*/React.createElement(WorkingStyleFeedbackModal, null)));
};
export default PublicWorkingStyle;